import type { RefundsAndCouponsSummary } from '@mentora/data-access'
import type { PriceFormatter } from '../price-formatter'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@mentora/ui-components'
import { getTranslations } from 'next-intl/server'

export async function RefundsCouponsPanel({
  data,
  formatPrice,
}: {
  data: RefundsAndCouponsSummary
  formatPrice: PriceFormatter
}) {
  const t = await getTranslations('dashboard.refundsCoupons')

  return (
    <div className="grid gap-3 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>{t('refundsTitle')}</CardTitle>
          <CardDescription>
            {t('refundsSummary', {
              count: data.refunds.count,
              total: formatPrice(data.refunds.totalCents),
            })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {data.refunds.topItems.length === 0
            ? (
                <p className="py-6 text-center text-sm text-muted-foreground">{t('refundsEmpty')}</p>
              )
            : (
                <ul className="space-y-3">
                  {data.refunds.topItems.map(r => (
                    <li
                      key={`${r.kind}:${r.id}`}
                      className="flex items-baseline justify-between gap-3 text-sm"
                    >
                      <span className="truncate text-foreground">{r.title}</span>
                      <span className="shrink-0 text-right">
                        <span className="font-mono tabular-nums text-foreground">
                          {formatPrice(r.refundedCents)}
                        </span>
                        <span className="ml-2 text-xs text-muted-foreground">
                          {t('refundCount', { count: r.refundCount })}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('couponsTitle')}</CardTitle>
          <CardDescription>
            {t('couponsSummary', {
              count: data.coupons.redemptionCount,
              total: formatPrice(data.coupons.totalDiscountCents),
            })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {data.coupons.topCoupons.length === 0
            ? (
                <p className="py-6 text-center text-sm text-muted-foreground">{t('couponsEmpty')}</p>
              )
            : (
                <ul className="space-y-3">
                  {data.coupons.topCoupons.map(c => (
                    <li
                      key={c.couponId}
                      className="flex items-baseline justify-between gap-3 text-sm"
                    >
                      <span className="flex flex-col">
                        <span className="font-mono text-foreground">{c.code}</span>
                        <span className="text-xs text-muted-foreground">
                          {t('couponPercent', { percent: c.discountPercent })}
                        </span>
                      </span>
                      <span className="shrink-0 text-right">
                        <span className="font-mono tabular-nums text-foreground">
                          {formatPrice(c.totalDiscountCents)}
                        </span>
                        <span className="ml-2 text-xs text-muted-foreground">
                          {t('couponUses', { count: c.uses })}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
        </CardContent>
      </Card>
    </div>
  )
}
